// Google Calendar events on our side: normalised from the API's shapes, placed
// on days, filtered by whose calendar they are on, and drawn in the grids.
// Cuplzap only reads Google; nothing about an event is ever written anywhere.

import type { CalendarOwner, DateStr, Settings, TimeStr } from '@/types'
import { addDays, dateOf, dateTimeFrom, timeOf } from './dates'
import { matchesEventWhose, type WhoseFilter } from './filters'

export type AccessRole = 'owner' | 'writer' | 'reader' | 'freeBusyReader'

export interface GCalendar {
  id: string
  name: string
  color: string
  accessRole: AccessRole
  primary: boolean
}

export interface CalEvent {
  id: string
  calendarId: string
  /** The series id of a repeating event. */
  seriesId: string | null
  title: string
  allDay: boolean
  /** First and last calendar day, inclusive; the API's exclusive all-day end is converted. */
  startDate: DateStr
  endDate: DateStr
  startTime: TimeStr | null
  endTime: TimeStr | null
  start: Date
  end: Date
  color: string
  htmlLink: string | null
  /** The video call link, from Meet or another conference. */
  videoLink: string | null
  location: string | null
  description: string | null
  visibility: string | null
}

export interface RawCalendar {
  id: string
  summary?: string
  summaryOverride?: string
  primary?: boolean
  accessRole?: string
  backgroundColor?: string
}

export interface RawEvent {
  id: string
  status?: string
  summary?: string
  description?: string
  location?: string
  start?: { date?: string; dateTime?: string }
  end?: { date?: string; dateTime?: string }
  htmlLink?: string
  recurringEventId?: string
  /** On a series' own event, read by events.get: its RRULE, EXDATE and RDATE lines. */
  recurrence?: string[]
  hangoutLink?: string
  conferenceData?: { entryPoints?: { entryPointType?: string; uri?: string }[] }
  visibility?: string
}

export function normalizeCalendar(raw: RawCalendar): GCalendar {
  const role = (raw.accessRole ?? 'reader') as AccessRole
  return {
    id: raw.id,
    name: raw.summaryOverride || raw.summary || raw.id,
    color: raw.backgroundColor || '#4285f4',
    accessRole: role,
    primary: !!raw.primary,
  }
}

/** A normalised event, or null for a cancelled one or one with no start. */
export function normalizeEvent(raw: RawEvent, cal: GCalendar): CalEvent | null {
  if (raw.status === 'cancelled' || !raw.start || !raw.end) return null
  const allDay = !!raw.start.date
  let start: Date
  let end: Date
  let startDate: DateStr
  let endDate: DateStr
  if (allDay) {
    startDate = raw.start.date!
    endDate = addDays(raw.end.date ?? raw.start.date!, -1)
    if (endDate < startDate) endDate = startDate
    start = dateTimeFrom(startDate, '00:00')
    end = dateTimeFrom(addDays(endDate, 1), '00:00')
  } else {
    start = new Date(raw.start.dateTime!)
    end = new Date(raw.end.dateTime ?? raw.start.dateTime!)
    startDate = dateOf(start)
    endDate = dateOf(end)
    // an event ending exactly at midnight belongs to the day before
    if (end.getHours() === 0 && end.getMinutes() === 0 && endDate > startDate) endDate = addDays(endDate, -1)
  }
  const video = raw.conferenceData?.entryPoints?.find((e) => e.entryPointType === 'video')?.uri ?? raw.hangoutLink ?? null
  return {
    id: raw.id,
    calendarId: cal.id,
    seriesId: raw.recurringEventId ?? null,
    title: raw.summary?.trim() || '(No title)',
    allDay,
    startDate,
    endDate,
    startTime: allDay ? null : timeOf(start),
    endTime: allDay ? null : timeOf(end),
    start,
    end,
    color: cal.color,
    htmlLink: raw.htmlLink ?? null,
    videoLink: video,
    location: raw.location ?? null,
    description: raw.description ?? null,
    visibility: raw.visibility ?? null,
  }
}

export function eventsOnDay<T extends CalEvent>(events: T[], date: DateStr): T[] {
  return events.filter((e) => e.startDate <= date && date <= e.endDate).sort(compareEvents)
}

export function compareEvents(a: CalEvent, b: CalEvent): number {
  if (a.allDay !== b.allDay) return a.allDay ? -1 : 1
  return a.start.getTime() - b.start.getTime() || a.title.localeCompare(b.title)
}

/** The app's guess: the primary calendar is Mine, one whose id is the other member's email is theirs, anything else is Both. */
export function guessOwner(cal: GCalendar, otherEmail: string | null): CalendarOwner {
  if (cal.primary) return 'me'
  if (otherEmail && cal.id.toLowerCase() === otherEmail.toLowerCase()) return 'other'
  return 'both'
}

export function ownerOf(cal: GCalendar, settings: Pick<Settings, 'calendarOwners'> | undefined, otherEmail: string | null): CalendarOwner {
  return settings?.calendarOwners[cal.id] ?? guessOwner(cal, otherEmail)
}

/** The primary calendar is shown by default; choices are saved in settings. */
export function isShown(cal: GCalendar, settings: Pick<Settings, 'shownCalendars'> | undefined): boolean {
  const choice = settings?.shownCalendars[cal.id]
  return choice === undefined ? cal.primary : choice
}

/** Whose filter for events: through whose calendar they are on. */
export function matchesEventFilter(owner: CalendarOwner, whose: WhoseFilter): boolean {
  return matchesEventWhose(owner, whose)
}

/** "18:30" and "19:30", or "All day". */
export function eventTimeLabels(ev: CalEvent): { start: string; end: string } {
  if (ev.allDay) return { start: 'All day', end: '' }
  return { start: ev.startTime ?? '', end: ev.endTime ?? '' }
}

export interface GridEventEntry {
  id: string
  title: string
  start: Date
  end: Date
  allDay: boolean
  editable: false
  extendedProps: { kind: 'event'; calendarId: string; eventId: string; owner: CalendarOwner }
}

/** Events within a range as FullCalendar inputs, coloured later by whose calendar they are on. Never editable. */
export function gridEventEntries(events: CalEvent[], from: DateStr, to: DateStr, ownerOfEvent: (e: CalEvent) => CalendarOwner): GridEventEntry[] {
  return events
    .filter((e) => e.startDate <= to && e.endDate >= from)
    .map((e) => ({
      id: `ev:${e.calendarId}:${e.id}`,
      title: e.title,
      start: e.start,
      end: e.end,
      allDay: e.allDay,
      editable: false as const,
      extendedProps: { kind: 'event' as const, calendarId: e.calendarId, eventId: e.id, owner: ownerOfEvent(e) },
    }))
}

/** Google descriptions can carry simple HTML; the card shows them as text, and a task made from the event keeps that text. */
export function stripHtml(s: string): string {
  return s
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .trim()
}
